/*
 * Copyright 2026 North Carolina State University
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LinkOffIcon from '@mui/icons-material/LinkOff';

const TOKEN_SOURCE = '\\{\\{\\s*([a-zA-Z0-9_]+)\\s*\\}\\}';

const MONO = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.85rem'
};

/**
 * Split one line into an alternating run of text and {{variable}} parts. The run
 * always starts and ends with a text part, even an empty one, so a part index
 * stays stable while the admin types and the line is reparsed.
 */
const parseLine = (line) => {
  const parts = [];
  const re = new RegExp(TOKEN_SOURCE, 'g');
  let cursor = 0;
  let match;
  while ((match = re.exec(line)) !== null) {
    parts.push({ type: 'text', value: line.slice(cursor, match.index) });
    parts.push({ type: 'variable', token: match[1] });
    cursor = match.index + match[0].length;
  }
  parts.push({ type: 'text', value: line.slice(cursor) });
  return parts;
};

const serializeParts = (parts) =>
  parts.map(part => (part.type === 'variable' ? `{{${part.token}}}` : part.value)).join('');

/**
 * Searchable variable list, shown wherever a variable can be picked. This is the
 * only place variables are browsed, so there is one habit to learn rather than a
 * separate reference table to scroll through.
 */
const VariablePicker = ({ request, variables, onClose }) => {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = variables.filter(v => (
      !needle ||
      v.token.toLowerCase().includes(needle) ||
      (v.label || '').toLowerCase().includes(needle) ||
      (v.group || '').toLowerCase().includes(needle)
    ));
    const byGroup = new Map();
    for (const v of matches) {
      const key = v.group || 'Other';
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(v);
    }
    return [...byGroup.entries()];
  }, [variables, query]);

  const close = () => {
    setQuery('');
    onClose();
  };

  const pick = (token) => {
    request.onPick(token);
    close();
  };

  return (
    <Popover
      open={!!request}
      anchorEl={request?.anchorEl || null}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, width: 380 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search variables"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Box>
      <Divider />
      <List dense sx={{ width: 380, maxHeight: 300, overflowY: 'auto', py: 0 }}>
        {groups.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No variable matches &ldquo;{query}&rdquo;.
            </Typography>
          </Box>
        )}
        {groups.map(([group, items]) => (
          <React.Fragment key={group}>
            <ListSubheader sx={{ lineHeight: '30px', fontWeight: 700 }}>{group}</ListSubheader>
            {items.map(v => (
              <ListItemButton
                key={v.token}
                selected={v.token === request?.currentToken}
                onClick={() => pick(v.token)}
              >
                <ListItemText
                  primary={`{{${v.token}}}`}
                  secondary={v.label}
                  primaryTypographyProps={{ sx: MONO }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            ))}
          </React.Fragment>
        ))}
      </List>
      {request?.onRemove && (
        <>
          <Divider />
          <Box sx={{ p: 1 }}>
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={() => {
                request.onRemove();
                close();
              }}
            >
              Remove this variable
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
};

const VariableChip = ({ token, known, onOpen }) => {
  const chip = (
    <Chip
      size="small"
      label={token}
      onClick={onOpen}
      onDelete={onOpen}
      deleteIcon={<ArrowDropDownIcon />}
      color={known ? 'primary' : 'warning'}
      variant={known ? 'filled' : 'outlined'}
      sx={{
        ...MONO,
        height: 22,
        cursor: 'pointer',
        '& .MuiChip-label': { px: 0.75 },
        '& .MuiChip-deleteIcon': { ml: -0.25, mr: 0 }
      }}
    />
  );
  return known
    ? chip
    : <Tooltip title="Not a known variable - it will be sent as-is">{chip}</Tooltip>;
};

/** Borderless inline input for the literal text between variables. */
const TextPart = ({ value, placeholder, fullWidth, onChange }) => {
  const chars = Math.max(value.length || (placeholder || '').length, 1) + 1;
  return (
    <InputBase
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      fullWidth={fullWidth}
      sx={{
        ...MONO,
        '& input': {
          font: 'inherit',
          p: 0,
          height: 22,
          ...(fullWidth ? {} : { width: `${chars}ch`, maxWidth: '100%' })
        }
      }}
    />
  );
};

/**
 * One line of the template: its literal text is editable in place and every
 * {{variable}} is a chip that opens the searchable picker.
 */
const TemplateLine = ({ line, knownTokens, onChangeLine, onOpenPicker, blankPlaceholder }) => {
  const parts = parseLine(line);
  const isPlainText = parts.length === 1;

  const setPart = (partIndex, part) => {
    const next = parseLine(line);
    if (part === null) next.splice(partIndex, 1);
    else next[partIndex] = part;
    onChangeLine(serializeParts(next));
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25, flex: 1 }}>
      {parts.map((part, partIndex) => (
        part.type === 'variable' ? (
          <VariableChip
            key={`v-${partIndex}`}
            token={part.token}
            known={knownTokens.has(part.token)}
            onOpen={(e) => onOpenPicker({
              anchorEl: e.currentTarget,
              currentToken: part.token,
              onPick: (token) => setPart(partIndex, { type: 'variable', token }),
              onRemove: () => setPart(partIndex, null)
            })}
          />
        ) : (
          <TextPart
            key={`t-${partIndex}`}
            value={part.value}
            fullWidth={isPlainText}
            placeholder={isPlainText ? blankPlaceholder : ''}
            onChange={(value) => setPart(partIndex, { type: 'text', value })}
          />
        )
      ))}
    </Box>
  );
};

/**
 * Editor for a business-office email template.
 *
 * The template string is the single source of truth: the guided view parses it
 * on every render and writes a whole new string back on every edit, so guided
 * and raw text views can never drift apart.
 */
const EmailTemplateEditor = ({ subject, body, variables, onSubjectChange, onBodyChange }) => {
  const [mode, setMode] = useState('guided');
  const [picker, setPicker] = useState(null);
  const [lineMenu, setLineMenu] = useState(null);
  const rawRef = useRef(null);
  const caretRef = useRef({ start: null, end: null });

  const knownTokens = useMemo(() => new Set(variables.map(v => v.token)), [variables]);
  const lines = useMemo(() => String(body || '').split('\n'), [body]);

  const commitLines = (nextLines) => onBodyChange(nextLines.join('\n'));

  const replaceLine = (index, nextLine) => {
    const next = [...lines];
    next[index] = nextLine;
    commitLines(next);
  };

  const insertLineBelow = (index, content) => {
    const next = [...lines];
    next.splice(index + 1, 0, content);
    commitLines(next);
  };

  const deleteLine = (index) => {
    if (lines.length === 1) return replaceLine(index, '');
    commitLines(lines.filter((_, i) => i !== index));
  };

  const appendVariableToLine = (index, token) => {
    const line = lines[index];
    const separator = line.length > 0 && !/\s$/.test(line) ? ' ' : '';
    replaceLine(index, `${line}${separator}{{${token}}}`);
  };

  // Raw view: remember where the caret was before the picker steals focus so a
  // variable lands where the admin was typing instead of at the end.
  const rememberCaret = () => {
    const el = rawRef.current;
    if (el) caretRef.current = { start: el.selectionStart, end: el.selectionEnd };
  };

  const insertAtCaret = (token) => {
    const text = String(body || '');
    const { start, end } = caretRef.current;
    const from = start ?? text.length;
    const to = end ?? from;
    const snippet = `{{${token}}}`;
    onBodyChange(`${text.slice(0, from)}${snippet}${text.slice(to)}`);
    const caret = from + snippet.length;
    caretRef.current = { start: caret, end: caret };
    requestAnimationFrame(() => {
      const el = rawRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const openLineMenu = (event, index) => {
    setLineMenu({ anchorEl: event.currentTarget, index });
  };

  // The gutter button anchors both the menu and any picker it opens, so it has
  // to outlive the menu closing.
  const fromLineMenu = (build) => {
    const { anchorEl, index } = lineMenu;
    setLineMenu(null);
    setPicker({ anchorEl, ...build(index) });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(e, value) => value && setMode(value)}
        >
          <ToggleButton value="guided">Guided</ToggleButton>
          <ToggleButton value="raw">Raw text</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {mode === 'guided'
            ? 'Click any blue chip to swap or remove that variable. Use the + in the left margin to add lines and variables anywhere.'
            : 'Edit the template as plain text. Insert a variable wherever your cursor is.'}
        </Typography>
      </Box>

      {/* Subject */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Subject
      </Typography>
      {mode === 'guided' ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mb: 2.5
          }}
        >
          <TemplateLine
            line={String(subject || '')}
            knownTokens={knownTokens}
            blankPlaceholder="Subject line"
            onChangeLine={onSubjectChange}
            onOpenPicker={setPicker}
          />
          <Tooltip title="Add a variable to the subject">
            <IconButton
              size="small"
              onClick={(e) => setPicker({
                anchorEl: e.currentTarget,
                onPick: (token) => {
                  const text = String(subject || '');
                  const separator = text.length > 0 && !/\s$/.test(text) ? ' ' : '';
                  onSubjectChange(`${text}${separator}{{${token}}}`);
                }
              })}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <TextField
          fullWidth
          size="small"
          value={subject || ''}
          onChange={(e) => onSubjectChange(e.target.value)}
          InputProps={{ sx: MONO }}
          sx={{ mb: 2.5 }}
        />
      )}

      {/* Body */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Body
      </Typography>

      {mode === 'guided' ? (
        <>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50',
              py: 1,
              maxHeight: 560,
              overflowY: 'auto'
            }}
          >
            {lines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pr: 1.5,
                  minHeight: 26,
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:hover .line-gutter': { opacity: 1 }
                }}
              >
                <Box
                  className="line-gutter"
                  sx={{ width: 30, flexShrink: 0, opacity: 0, transition: 'opacity 120ms' }}
                >
                  <Tooltip title="Add or remove">
                    <IconButton size="small" onClick={(e) => openLineMenu(e, index)}>
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TemplateLine
                  line={line}
                  knownTokens={knownTokens}
                  onChangeLine={(next) => replaceLine(index, next)}
                  onOpenPicker={setPicker}
                />
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => commitLines([...lines, ''])}
          >
            Add a line at the end
          </Button>
        </>
      ) : (
        <>
          <TextField
            fullWidth
            multiline
            minRows={20}
            inputRef={rawRef}
            value={body || ''}
            onChange={(e) => onBodyChange(e.target.value)}
            onSelect={rememberCaret}
            onKeyUp={rememberCaret}
            onClick={rememberCaret}
            InputProps={{ sx: { ...MONO, lineHeight: 1.6 } }}
          />
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onMouseDown={rememberCaret}
            onClick={(e) => setPicker({
              anchorEl: e.currentTarget,
              onPick: insertAtCaret
            })}
          >
            Insert a variable at the cursor
          </Button>
        </>
      )}

      <Menu
        open={!!lineMenu}
        anchorEl={lineMenu?.anchorEl || null}
        onClose={() => setLineMenu(null)}
      >
        <MenuItem onClick={() => fromLineMenu(index => ({
          onPick: (token) => appendVariableToLine(index, token)
        }))}>
          Add a variable to this line
        </MenuItem>
        <MenuItem onClick={() => fromLineMenu(index => ({
          onPick: (token) => insertLineBelow(index, `{{${token}}}`)
        }))}>
          Add a variable on a new line below
        </MenuItem>
        <MenuItem onClick={() => {
          insertLineBelow(lineMenu.index, '');
          setLineMenu(null);
        }}>
          Insert an empty line below
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            deleteLine(lineMenu.index);
            setLineMenu(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete this line
        </MenuItem>
      </Menu>

      <VariablePicker
        request={picker}
        variables={variables}
        onClose={() => setPicker(null)}
      />
    </Box>
  );
};

export default EmailTemplateEditor;
